import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import type { Person } from '@contracts/people';
import { useMe, useOrg, usePeople } from '../api/queries';
import {
  BalanceText,
  Broken,
  Card,
  Empty,
  Icon,
  Rows,
  SearchField,
  SkeletonRows,
  Tabs,
  Who,
} from '../ui';
import { initialsOf } from '../ui';
import { since } from '../lib/time';
import { companyOf, domainOf } from '../lib/company';
import { useTab, useUrlText } from '../lib/url-tab';

type Tab = 'people' | 'companies';

/**
 * Board 10 — every counterparty, with what is open on each side. Two
 * balances per row, always the same two, always pressable: a balance lands
 * on that person already filtered to that side; the row lands unfiltered.
 */
export function People(): ReactElement {
  const [tab, setTab] = useTab<Tab>('tab', ['people', 'companies'], 'people');
  const [filter, setFilter] = useUrlText('q');
  const people = usePeople();
  const me = useMe();
  const org = useOrg();
  const navigate = useNavigate();
  const home = me.data
    ? { domain: domainOf(me.data.email), name: org.data?.name ?? 'Your workspace' }
    : null;
  const q = filter.trim().toLowerCase();
  const shownPeople = (people.data ?? []).filter(
    (p) =>
      !q ||
      p.email.includes(q) ||
      (p.name?.toLowerCase().includes(q) ?? false) ||
      (companyOf(p.company, home)?.toLowerCase().includes(q) ?? false),
  );
  const companies = useMemo(() => rollUp(people.data ?? []), [people.data]);
  const shownCompanies = companies.filter(
    (c) => !q || (companyOf(c.domain, home)?.toLowerCase().includes(q) ?? false),
  );

  return (
    <div className="page stack-lg">
      <div className="page-head">
        <h1 className="t-title">People</h1>
        <div style={{ width: 220 }}>
          <SearchField
            placeholder="Filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            kbd=""
          />
        </div>
      </div>
      <Tabs
        label="People or companies"
        value={tab}
        onChange={setTab}
        items={[
          { key: 'people', label: 'People', count: people.data?.length },
          { key: 'companies', label: 'Companies', count: companies.length },
        ]}
      />

      {tab === 'people' ? (
        <Card flush>
          {people.isPending ? (
            <SkeletonRows rows={5} />
          ) : people.isError ? (
            <Broken what="People" retry={() => void people.refetch()} />
          ) : shownPeople.length === 0 ? (
            <Empty lead={q ? `Nobody matches “${filter}”.` : 'Nobody yet.'}>
              {q
                ? undefined
                : 'The people you meet appear here after the first recorded meeting, with what is open on each side.'}
            </Empty>
          ) : (
            <Rows>
              {shownPeople.map((p) => (
                <div
                  key={p.email}
                  className="row"
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/people/${encodeURIComponent(p.email)}`)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && navigate(`/people/${encodeURIComponent(p.email)}`)
                  }
                  style={{ cursor: 'pointer', minHeight: 60 }}
                >
                  <span className="avatar lg">{initialsOf(p.name, p.email)}</span>
                  <div className="row-main">
                    <span className="row-title">{p.name ?? p.email}</span>
                    <span className="mono">
                      {companyOf(p.company, home) ?? 'personal address'}
                      {home && p.company && p.company.toLowerCase() !== home.domain
                        ? ' · guest'
                        : ''}{' '}
                      · last spoke {since(p.lastMetAt)} · {p.meetingCount}{' '}
                      {p.meetingCount === 1 ? 'meeting' : 'meetings'}
                    </span>
                  </div>
                  <BalanceText iOwe={p.balance.iOwe} theyOwe={p.balance.theyOwe} />
                </div>
              ))}
            </Rows>
          )}
        </Card>
      ) : null}

      {tab === 'companies' ? (
        <Card flush>
          {shownCompanies.length === 0 ? (
            <Empty lead="No company yet.">
              A company row rolls up its people — it appears once you have met somebody with a
              company address.
            </Empty>
          ) : (
            <Rows>
              {shownCompanies.map((c) => (
                <div key={c.domain} className="row" style={{ minHeight: 60 }}>
                  <span className="avatar room lg">
                    <Icon name="company" size={17} />
                  </span>
                  <div className="row-main">
                    <span className="row-title">{companyOf(c.domain, home)}</span>
                    <span className="row-sub">
                      <span className="mono">
                        company · {c.people.length} {c.people.length === 1 ? 'person' : 'people'} ·{' '}
                        {c.meetings} meetings
                      </span>
                      {c.people.slice(0, 4).map((p) => (
                        <Who
                          key={p.email}
                          email={p.email}
                          name={p.name}
                          short
                          to={`/people/${encodeURIComponent(p.email)}`}
                        />
                      ))}
                    </span>
                  </div>
                  <BalanceText iOwe={c.iOwe} theyOwe={c.theyOwe} them="they owe you" />
                </div>
              ))}
            </Rows>
          )}
        </Card>
      ) : null}
      <p className="t-secondary">
        A series row describes itself like a machine: instances, language, whether it records. A
        company row rolls up its people. <Link to="/channels">Browse by collection instead →</Link>
      </p>
    </div>
  );
}

function rollUp(people: Person[]) {
  const map = new Map<
    string,
    { domain: string; people: Person[]; meetings: number; iOwe: number; theyOwe: number }
  >();
  for (const p of people) {
    if (!p.company) continue;
    const c = map.get(p.company) ?? {
      domain: p.company,
      people: [],
      meetings: 0,
      iOwe: 0,
      theyOwe: 0,
    };
    c.people.push(p);
    c.meetings += p.meetingCount;
    c.iOwe += p.balance.iOwe;
    c.theyOwe += p.balance.theyOwe;
    map.set(p.company, c);
  }
  return [...map.values()].sort((a, b) => b.meetings - a.meetings);
}
